/**
 * CARRY PROGRESSION — distance as the primary progression lever.
 *
 * A loaded carry is not rep work. Programs author them as `sets: 4,
 * reps: '30-50m'` or `reps: '40m'`, and the logger stores METRES in the `reps`
 * field. Until now nothing in the engine ever moved that number: the entire
 * progression stack (auto-progress, statistical trend, double progression,
 * plateau detection) reasons about weight, so a carry's only adjustable term
 * was load — and the athlete's actual progress, walking farther under the same
 * bar, was invisible to it. See lib/__tests__/carryPlateauPrescription.test.ts
 * for the production damage that caused.
 *
 * The rule here is ordinary double progression with distance standing in for
 * reps, which is the standard prescription for carries:
 *
 *   1. Work UP the authored distance range at a fixed load.
 *   2. On completing the top of the range at a manageable effort, add LOAD and
 *      reset distance to the bottom of the range.
 *   3. If effort is high or sets are failing, hold — never cut.
 *
 * Why hold instead of cut (step 3): the limiter on a carry is usually grip or
 * trunk endurance, and it fails abruptly rather than degrading. An athlete who
 * drops the bar at 35 of 40 m has not lost capacity the way a missed squat
 * rep signals it — they ran out of hands. Cutting load there teaches the
 * engine to chase noise, which is exactly the failure that produced the
 * escalating phantom deload ladder on Farmer's Walk.
 *
 * Pure and I/O-free so it can be tested against real logged history.
 */

/** Parsed distance prescription, in metres. */
export interface CarryDistanceSpec {
  /** Bottom of the authored range (or the single authored value). */
  min: number;
  /** Top of the authored range. Equals `min` when a single value was authored. */
  max: number;
  /** True when the program authored an explicit range like '30-50m'. */
  isRange: boolean;
}

export interface CarryHistoryPoint {
  date: Date;
  /** Load carried, lb. */
  weight: number;
  /** Distance covered for this set, METRES (stored in the log's `reps` field). */
  distance: number;
  rpe: number | null;
  /** Whether the set was completed as prescribed. */
  wasSuccessful: boolean;
}

export type CarryProgressionAction =
  | 'extend-distance'
  | 'add-load'
  | 'hold'
  | 'insufficient-data';

export interface CarryProgressionResult {
  action: CarryProgressionAction;
  /** Distance to prescribe per round, metres. */
  distance: number;
  /** Load to prescribe, lb. */
  weight: number;
  /** Plain-language explanation for the athlete. Never says "reps". */
  reason: string;
  confidence: 'high' | 'medium' | 'low';
}

/**
 * When a program authors a single distance with no range (e.g. '40m'), we still
 * need a ceiling before rolling the progression over into load. 1.5× the
 * authored distance keeps a 40 m carry inside a sane 40–60 m working band
 * rather than marching it out to a completely different training effect.
 */
const IMPLICIT_RANGE_FACTOR = 1.5;

/** Smallest distance step worth prescribing. Below this it's measurement noise. */
const MIN_STEP_M = 5;

/** Effort at or above which we stop adding and let the athlete consolidate. */
const HARD_EFFORT_RPE = 9;

/** Effort at or below which adding distance is clearly warranted. */
const EASY_EFFORT_RPE = 8;

/** Load bump when distance tops out, as a fraction. Carries tolerate ~5-10%. */
const LOAD_STEP = 0.05;

/**
 * Parse an authored carry distance, e.g. '30-50m', '40m', '4 × 40m', '40'.
 * Returns null when the string carries no distance we can trust.
 */
export function parseCarryDistance(setsReps: string): CarryDistanceSpec | null {
  if (!setsReps) return null;

  // Strip a leading "sets ×" so '4 × 30-50m' doesn't read 4 as the distance.
  const afterSets = setsReps.replace(/^\s*\d+\s*[x×]\s*/i, '');

  const range = afterSets.match(/(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)\s*m?\b/i);
  if (range) {
    const min = parseFloat(range[1]);
    const max = parseFloat(range[2]);
    if (min > 0 && max >= min) return { min, max, isRange: true };
  }

  const single = afterSets.match(/(\d+(?:\.\d+)?)\s*m\b/i);
  if (single) {
    const v = parseFloat(single[1]);
    if (v > 0) return { min: v, max: v, isRange: false };
  }

  // Bare number with no unit — only trust it if it's a plausible carry
  // distance. A '4 × 10' with no 'm' is far more likely to be rep work that
  // was misclassified than a 10 m carry.
  const bare = afterSets.match(/^(\d+(?:\.\d+)?)\s*$/);
  if (bare) {
    const v = parseFloat(bare[1]);
    if (v >= 15) return { min: v, max: v, isRange: false };
  }

  return null;
}

/** Effective ceiling for a spec — explicit range top, or the implicit band. */
export function carryDistanceCeiling(spec: CarryDistanceSpec): number {
  return spec.isRange ? spec.max : Math.round(spec.min * IMPLICIT_RANGE_FACTOR);
}

/**
 * Group flat set-level history into per-session summaries. A carry session is
 * judged on the distance the athlete actually sustained across the work, so we
 * use the MINIMUM completed distance rather than the best set — finishing three
 * rounds at 40 m and one at 25 m is not a 40 m session.
 */
function summariseSessions(history: CarryHistoryPoint[]): Array<{
  date: string;
  weight: number;
  sustainedDistance: number;
  avgRPE: number | null;
  allCompleted: boolean;
}> {
  const byDate = new Map<string, CarryHistoryPoint[]>();
  for (const h of history) {
    const key = h.date.toISOString().slice(0, 10);
    const bucket = byDate.get(key);
    if (bucket) bucket.push(h);
    else byDate.set(key, [h]);
  }

  return Array.from(byDate.entries())
    .map(([date, sets]) => {
      const rpes = sets.map(s => s.rpe).filter((r): r is number => r != null && r > 0);
      const completed = sets.filter(s => s.wasSuccessful);
      return {
        date,
        weight: Math.max(...sets.map(s => s.weight), 0),
        // Sustained across completed work; 0 if nothing was completed.
        sustainedDistance: completed.length > 0
          ? Math.min(...completed.map(s => s.distance))
          : 0,
        avgRPE: rpes.length > 0 ? rpes.reduce((a, b) => a + b, 0) / rpes.length : null,
        allCompleted: sets.length > 0 && completed.length === sets.length,
      };
    })
    .sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Decide the next carry prescription.
 *
 * @param spec       authored distance prescription for this session
 * @param baseWeight load the engine would otherwise prescribe, lb
 * @param history    logged set-level history for this carry, any order
 */
export function getCarryProgression(
  spec: CarryDistanceSpec,
  baseWeight: number,
  history: CarryHistoryPoint[],
): CarryProgressionResult {
  const ceiling = carryDistanceCeiling(spec);
  const sessions = summariseSessions(history);
  const last = sessions[sessions.length - 1];

  if (!last || last.sustainedDistance <= 0) {
    return {
      action: 'insufficient-data',
      distance: spec.min,
      weight: baseWeight,
      reason: `Starting at ${spec.min} m — log a session to start progressing distance.`,
      confidence: 'low',
    };
  }

  const effort = last.avgRPE;
  const struggled = !last.allCompleted || (effort != null && effort >= HARD_EFFORT_RPE);

  // Hold: high effort or dropped sets. Consolidate at what was last sustained,
  // never below the authored floor.
  if (struggled) {
    const held = Math.max(spec.min, Math.min(last.sustainedDistance, ceiling));
    const why = !last.allCompleted
      ? `you didn't finish every round last time`
      : `last session ran RPE ${effort!.toFixed(1)}`;
    return {
      action: 'hold',
      distance: held,
      weight: baseWeight,
      reason: `Holding ${held} m at ${baseWeight} lb — ${why}. Own this distance before it grows.`,
      confidence: 'high',
    };
  }

  const sustained = last.sustainedDistance;

  // Topped out the range at a manageable effort → add load, reset distance.
  if (sustained >= ceiling) {
    const nextWeight = Math.round((baseWeight * (1 + LOAD_STEP)) / 5) * 5;
    const bumped = nextWeight > baseWeight ? nextWeight : baseWeight + 5;
    return {
      action: 'add-load',
      distance: spec.min,
      weight: bumped,
      reason: `You carried ${sustained} m — the top of the range. Load goes to ${bumped} lb and distance resets to ${spec.min} m.`,
      confidence: 'high',
    };
  }

  // Room left in the range and effort allows → extend distance, hold load.
  if (effort == null || effort <= EASY_EFFORT_RPE) {
    const step = Math.max(MIN_STEP_M, Math.round((sustained * 0.1) / 5) * 5);
    const next = Math.min(ceiling, sustained + step);
    if (next <= sustained) {
      return {
        action: 'hold',
        distance: sustained,
        weight: baseWeight,
        reason: `Holding ${sustained} m at ${baseWeight} lb.`,
        confidence: 'medium',
      };
    }
    return {
      action: 'extend-distance',
      distance: next,
      weight: baseWeight,
      // No-RPE case is a genuinely weaker signal — say so rather than implying
      // we read an effort we never got.
      reason: effort == null
        ? `Distance up to ${next} m at ${baseWeight} lb — you completed every round last time.`
        : `Distance up to ${next} m at ${baseWeight} lb — last session sat at RPE ${effort.toFixed(1)}.`,
      confidence: effort == null ? 'medium' : 'high',
    };
  }

  // Effort between the easy and hard thresholds: repeat the distance.
  return {
    action: 'hold',
    distance: Math.max(spec.min, sustained),
    weight: baseWeight,
    reason: `Repeating ${Math.max(spec.min, sustained)} m at ${baseWeight} lb — RPE ${effort.toFixed(1)} says bank this one first.`,
    confidence: 'medium',
  };
}

/** Rewrite an authored sets_reps string with a new per-round distance. */
export function applyCarryDistance(setsReps: string, distance: number): string {
  const setsMatch = setsReps.match(/^\s*(\d+)\s*[x×]/i);
  const sets = setsMatch ? setsMatch[1] : null;
  return sets ? `${sets} × ${distance}m` : `${distance}m`;
}
